import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { faArrowsRotate, faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { ButtonComponent, IconComponent, TooltipDirective } from '@pure/hive';
import { CopilotChatController } from '../services/copilot-chat-controller';
import { CopilotPageSuggestionsService, SuggestionsState } from '../services/copilot-page-suggestions.service';
import { CopilotSidePanelService } from '../services/copilot-side-panel.service';
import { trackQuestionClicked } from '../copilot-question-analytics';

const SKELETON_PLACEHOLDERS = [0, 1, 2];
const IDLE_STATE: SuggestionsState = { state: 'idle', questions: [] };

@Component({
    selector: 'copilot-page-suggestions',
    standalone: true,
    templateUrl: './copilot-page-suggestions.component.html',
    styleUrl: './copilot-page-suggestions.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, IconComponent, TooltipDirective],
    // Idle renders nothing, but the host's `display: block` + 16px padding would still occupy
    // 32px above the input bar — the same problem `:host([hidden])` solves for the flag-off case.
    host: { '[class.is-empty]': 'viewState().state === "idle"' },
})
export class CopilotPageSuggestionsComponent implements OnInit {
    private readonly controller = inject(CopilotChatController);
    private readonly suggestionsService = inject(CopilotPageSuggestionsService);
    private readonly sidePanel = inject(CopilotSidePanelService);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Render outside the CoPilot side panel (e.g. the Subscription Assessment
     * "Ask CoPilot" card). The panel-driven trigger only fetches while the panel
     * is open, so a standalone instance fetches its own suggestions on init and
     * opens the side panel when a pill is clicked. Default `false` keeps the
     * in-panel behavior (shared state + submit into the already-open panel).
     */
    readonly outsidePanel = input(false);

    private readonly sharedState = toSignal(this.suggestionsService.state$, { initialValue: IDLE_STATE });
    private readonly standaloneState = signal<SuggestionsState>(IDLE_STATE);

    /** State the template renders: local suggestions when standalone, else the shared panel state. */
    protected readonly viewState = computed(() => (this.outsidePanel() ? this.standaloneState() : this.sharedState()));

    protected readonly skeletonPlaceholders = SKELETON_PLACEHOLDERS;
    protected readonly faSparkles = faSparkles;
    protected readonly faRefresh = faArrowsRotate;

    ngOnInit(): void {
        if (this.outsidePanel()) {
            this.loadStandaloneSuggestions();
        }
    }

    protected onPillClick(question: string): void {
        // The only outside-panel host today is the Subscription Assessment "Ask CoPilot" card, so
        // that mode reports itself as the assessment card to keep the two surfaces apart in Amplitude.
        trackQuestionClicked(question, this.outsidePanel() ? 'assessment card' : 'page suggestion');
        // Outside the panel the chat isn't visible — open the panel so the answer shows. The
        // question rides along with that open rather than being submitted here, so it waits on
        // the Copilot welcome modal too and a declined question is never sent.
        if (this.outsidePanel()) {
            this.sidePanel.openWithMessage(question);
            return;
        }
        this.controller.submitMessage(question);
    }

    protected onRefresh(): void {
        if (this.outsidePanel()) {
            this.loadStandaloneSuggestions();
        } else {
            this.suggestionsService.refresh();
        }
    }

    private loadStandaloneSuggestions(): void {
        this.suggestionsService
            .fetchSuggestionsOnce()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(state => this.standaloneState.set(state));
    }
}
